import { api } from './client'
import type {
  AccountOverview,
  AppNotification,
  BasketComparison,
  BasketMode,
  Category,
  CheapestProductType,
  Collection,
  CurrentUser,
  DashboardStats,
  ImportResult,
  Leaflet,
  LeafletDetail,
  LeafletPageDetail,
  LeafletSummary,
  LeafletStatus,
  McpOverview,
  McpTokenCreated,
  NearestBranch,
  OauthAuthorizeRequest,
  OauthClientInfo,
  Offer,
  OfferGroup,
  OfferSort,
  PlannerRequest,
  PlannerResult,
  PriceObservation,
  PriceObservationPayload,
  PriceObservationResult,
  PriceOverview,
  Product,
  ProductHistory,
  ProductTypeComparison,
  ProductTypeSummary,
  QueuedPage,
  SavedShoppingList,
  ShoppingListItem,
  Store,
  StoreBranch,
  User,
  UserSummary,
  Visit,
} from '@/types/api'

export interface UserListParams {
  page?: number
  itemsPerPage?: number
  email?: string
  firstName?: string
  lastname?: string
  /** Řazení podle konvence API Platform, např. `order[email]: 'asc'`. */
  [key: string]: string | number | boolean | undefined | null
}

export interface UserPayload {
  email: string
  firstName: string
  lastname: string
  roles?: string[]
  plainPassword?: string
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: UserSummary }>('/api/auth/login', { email, password }),
  logout: () => api.post<{ status: string }>('/api/auth/logout'),
  requestPasswordReset: (email: string) =>
    api.post<{ status: string; message: string }>('/api/auth/password-reset/request', { email }),
  verifyResetToken: (token: string) =>
    api.get<{ valid: boolean }>(`/api/auth/password-reset/verify/${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) =>
    api.post<{ status: string; message: string }>('/api/auth/password-reset', { token, password }),
}

export const meApi = {
  profile: () => api.get<CurrentUser>('/api/me'),
  /** Uživatel si mění jen jméno — e-mail a role spravuje administrátor. */
  updateProfile: (firstName: string, lastname: string) =>
    api.patch<{ id: number; fullname: string }>('/api/me', { firstName, lastname }),
  overview: (days = 14) => api.get<AccountOverview>('/api/me/overview', { days }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put<{ status: string; message: string }>('/api/me/password', { currentPassword, newPassword }),
  revokeOtherSessions: () => api.delete<{ status: string; revoked: number }>('/api/me/sessions'),
}

export const usersApi = {
  list: (params: UserListParams): Promise<Collection<User>> => api.collection<User>('/api/users', params),
  get: (id: number) => api.get<User>(`/api/users/${id}`),
  create: (payload: UserPayload) => api.post<User>('/api/users', payload),
  update: (id: number, payload: Partial<UserPayload>) => api.patch<User>(`/api/users/${id}`, payload),
  remove: (id: number) => api.delete<void>(`/api/users/${id}`),
  import: (emails: string, roles: string[], sendInvites: boolean) =>
    api.post<ImportResult>('/api/users/import', { emails, roles, sendInvites }),
}

export const notificationsApi = {
  list: (params: Record<string, string | number | boolean | undefined>) =>
    api.collection<AppNotification>('/api/notifications', params),
  markRead: (id: number, read: boolean) => api.patch<AppNotification>(`/api/notifications/${id}`, { read }),
  remove: (id: number) => api.delete<void>(`/api/notifications/${id}`),
  unreadCount: () => api.get<{ count: number }>('/api/notifications/unread-count'),
  readAll: () => api.post<{ status: string; marked: number }>('/api/notifications/read-all'),
}

export const statsApi = {
  dashboard: (days = 30) => api.get<DashboardStats>('/api/stats/dashboard', { days }),
}

export const visitsApi = {
  list: (params: Record<string, string | number | undefined>) => api.collection<Visit>('/api/visits', params),
  track: (pageType: string, pageId = 0) =>
    api.post<{ status: string; visitCount: number }>('/api/visits/track', { pageType, pageId }),
}

/** Společné stránkování katalogu — API ho převádí na limit/offset. */
export interface CatalogPaging {
  page?: number
  itemsPerPage?: number
}

export interface LeafletListParams extends CatalogPaging {
  store?: string
  activeOn?: string
  status?: LeafletStatus | ''
}

export interface OfferListParams extends CatalogPaging {
  query?: string
  store?: string
  category?: string
  productType?: string
  activeOn?: string
  promoOnly?: boolean
  sort?: OfferSort
}

export interface CheapestByTypeParams {
  activeOn?: string
  category?: string
  query?: string
  limit?: number
  offset?: number
}

export interface PriceOverviewParams {
  type: string
  stores?: string[]
  sameBrand?: boolean
  /** Datum YYYY-MM-DD, výchozí dnešek. */
  asOf?: string
}

export interface BasketParams {
  types: string[]
  stores: string[]
  mode?: BasketMode
  sameBrand?: boolean
  asOf?: string
}

export interface ProductListParams extends CatalogPaging {
  query?: string
  category?: string
  productType?: string
}

/**
 * Katalog letáků, nabídek a historie cen. Seznamy přicházejí jako holá pole
 * bez celkového počtu — stránkuje se „načíst další“ (viz useLoadMore).
 */
export const catalogApi = {
  stores: () => api.get<Store[]>('/api/catalog/stores'),
  categories: () => api.get<Category[]>('/api/catalog/categories'),
  leaflets: (params: LeafletListParams = {}) => api.get<Leaflet[]>('/api/catalog/leaflets', { ...params }),
  leafletsSummary: (params: Omit<LeafletListParams, keyof CatalogPaging> = {}) =>
    api.get<LeafletSummary>('/api/catalog/leaflets/summary', { ...params }),
  leaflet: (id: number) => api.get<LeafletDetail>(`/api/catalog/leaflets/${id}`),
  page: (id: number) => api.get<LeafletPageDetail>(`/api/catalog/pages/${id}`),
  /** Obrázek jde rovnou do `<img src>`; přihlašovací cookie pošle prohlížeč sám. */
  pageImageUrl: (id: number) => `/api/catalog/pages/${id}/image`,
  queue: () => api.get<QueuedPage[]>('/api/catalog/queue'),
  offers: ({ promoOnly, ...params }: OfferListParams = {}) =>
    api.get<Offer[]>('/api/catalog/offers', { ...params, promoOnly: promoOnly ? 1 : undefined }),
  offerGroups: ({ promoOnly, ...params }: OfferListParams = {}) =>
    api.get<OfferGroup[]>('/api/catalog/offers/groups', { ...params, promoOnly: promoOnly ? 1 : undefined }),
  products: (params: ProductListParams = {}) => api.get<Product[]>('/api/catalog/products', { ...params }),
  productHistory: (id: number, params: { store?: string; since?: string } = {}) =>
    api.get<ProductHistory>(`/api/catalog/products/${id}/history`, { ...params }),
  /** API bez itemsPerPage vrací jen 50 druhů, výběr potřebuje víc. */
  productTypes: (params: { category?: string; query?: string } = {}) =>
    api.get<ProductTypeSummary[]>('/api/catalog/product-types', { itemsPerPage: 200, ...params }),
  /** Ilustrační fotka ke každému druhu, který nějakou má (druh => URL výřezu). */
  productTypeImages: (types: string[]) =>
    api.get<Record<string, string> | []>('/api/catalog/product-types/images', { 'types[]': types }),
  compare: (params: { type: string; activeOn?: string; since?: string }) =>
    api.get<ProductTypeComparison>('/api/catalog/product-types/compare', { ...params }),
  /**
   * Nejlevnější nabídka každého druhu zboží napříč značkami a obchody.
   * API nestránkuje (offset ignoruje), proto se bere rovnou maximum 200.
   */
  cheapestByType: (params: CheapestByTypeParams = {}) =>
    api.get<CheapestProductType[]>('/api/catalog/product-types/cheapest', { ...params }),
  /** Běžná a akční cena druhu zboží v jednotlivých obchodech (bez obchodů = všechny). */
  priceOverview: ({ type, stores = [], sameBrand, asOf }: PriceOverviewParams) =>
    api.get<PriceOverview>('/api/catalog/price-overview', {
      type,
      'stores[]': stores,
      sameBrand: sameBrand ? 1 : undefined,
      asOf,
    }),
  /**
   * Srovnání obchodů na košíku druhů zboží. Dvě oddělené metriky:
   * `regular` (běžný nákup bez akcí, výchozí) a `promo` (akční výhodnost).
   */
  basket: ({ types, stores, mode = 'regular', sameBrand, asOf }: BasketParams) =>
    api.get<BasketComparison>('/api/catalog/basket', {
      'types[]': types,
      'stores[]': stores,
      mode,
      sameBrand: sameBrand ? 1 : undefined,
      asOf,
    }),
  productObservations: (id: number) => api.get<PriceObservation[]>(`/api/catalog/products/${id}/observations`),
  /** Ruční zápis ceny z regálu, účtenky nebo e-shopu — jen ROLE_ADMIN (jinak 403). */
  addObservation: (payload: PriceObservationPayload) =>
    api.post<PriceObservationResult>('/api/catalog/price-observations', payload),
}

export const mcpApi = {
  overview: () => api.get<McpOverview>('/api/me/mcp'),
  createToken: (name: string) => api.post<McpTokenCreated>('/api/me/mcp/tokens', { name }),
  revokeToken: (id: number) => api.delete<void>(`/api/me/mcp/tokens/${id}`),
}

export const oauthApi = {
  client: (clientId: string, redirectUri: string) =>
    api.get<OauthClientInfo>(`/api/oauth/clients/${encodeURIComponent(clientId)}`, { redirect_uri: redirectUri }),
  authorize: (payload: OauthAuthorizeRequest) => api.post<{ redirect: string }>('/api/oauth/authorize', payload),
}

/** Prodejny na mapě (OpenStreetMap) a nejbližší prodejny autem (OSRM). */
export const mapApi = {
  branches: (store?: string) => api.get<StoreBranch[]>('/api/map/branches', { store }),
  nearest: (params: { lat: number; lon: number; store?: string; limit?: number }) =>
    api.get<NearestBranch[]>('/api/map/branches/nearest', { ...params }),
}

export const plannerApi = {
  plan: (request: PlannerRequest) => api.post<PlannerResult>('/api/planner', request),
}

export const shoppingListApi = {
  list: () => api.get<SavedShoppingList[]>('/api/shopping-lists'),
  create: (name: string, items: ShoppingListItem[]) => api.post<SavedShoppingList>('/api/shopping-lists', { name, items }),
  update: (id: number, name: string, items: ShoppingListItem[]) =>
    api.put<SavedShoppingList>(`/api/shopping-lists/${id}`, { name, items }),
  remove: (id: number) => api.delete<null>(`/api/shopping-lists/${id}`),
}
